import { Volume, OptLogVolumeActions } from '~/shared/types/api';
import type { GraphResponse } from '~/shared/types/api';
import { SelectOption } from '~/shared/utils/types';

type GraphDataPoint = NonNullable<GraphResponse['utilizationGraph']>[number];

const BYTES_PER_GIB = 1024 ** 3;

export function nullForNA(val: unknown): unknown {
  if (val == null) return null;
  if (typeof val === 'number' && isNaN(val)) return null;
  if (val === 'N/A') return null;
  if (typeof val === 'string' && val.trim().toUpperCase() === 'N/A') return null;
  return val;
}

export function formatExportFixed(value: unknown, fractionDigits = 2): string | null {
  const cleaned = nullForNA(value);
  if (cleaned == null) return null;
  const n = typeof cleaned === 'number' ? cleaned : Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(fractionDigits);
}

export function sanitizeForFilename(str: string): string {
  return str.replace(/[^a-zA-Z0-9-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

function groupToSnakeCase(group: string): string {
  if (group.startsWith('tag:')) {
    return `volume_tag_${group.slice(4)}`;
  }
  return group.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function normalizeFilterName(group: string, name: string): string {
  if (group === 'autoscalingState') {
    const lower = name.toLowerCase();
    if (lower === 'on') return 'active';
    if (lower === 'off') return 'inactive';
  }
  return name;
}

/**
 * Build an export filename.
 * Pattern: `<account>_Datafy_<contentName>_<filtersSummary>_<YYYY-MM-DD>`
 */
export function generateExportFilename(
  accountName: string,
  contentName: string,
  activeFilters: SelectOption[],
  date: Date = new Date()
): string {
  const account = sanitizeForFilename(accountName || 'Account');
  const content = sanitizeForFilename(contentName);
  let filtersSummary: string;
  if (!activeFilters || activeFilters.length === 0) {
    filtersSummary = 'no_filters';
  } else if (activeFilters.length <= 2) {
    filtersSummary = activeFilters
      .map((f) => sanitizeForFilename(`${groupToSnakeCase(f.group)}_${normalizeFilterName(f.group, f.name)}`))
      .join('_and_');
  } else {
    filtersSummary = `${activeFilters.length}_filters`;
  }
  const dateStr = date.toISOString().slice(0, 10); // YYYY-MM-DD
  return `${account}_Datafy_${content}_${filtersSummary}_${dateStr}`;
}

export function downloadFile(
  content: string | Blob,
  filename: string,
  mimeType: string
): void {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function injectDateParams(url: string, startDate: string, endDate: string): string {
  const [path, search] = url.split('?');
  const params = new URLSearchParams(search || '');
  params.set('start_date', startDate);
  params.set('end_date', endDate);
  return `${path}?${params.toString()}`;
}

function escapeCell(value: unknown): string {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Serialize an array of row objects to an RFC-4180 CSV string. */
export function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const headerLine = headers.map(escapeCell).join(',');
  const dataLines = rows.map((row) => headers.map((h) => escapeCell(row[h])).join(','));
  return [headerLine, ...dataLines].join('\r\n');
}


function resolveVolumeCurrentSizeBytes(volume: Volume): number | null {
  return volume.datafyDiskSize ??
    volume.lastUtilizationData?.datafyDiskSize ??
    volume.diskSize ??
    null;
}

function resolveVolumeUtilization(volume: Volume): number | null {
  const raw =
    nullForNA(volume.utilization) ??
    nullForNA(volume.lastUtilizationData?.datafyDiskUsedPercentage);
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  // Stored as percent (0-100); normalize to absolute 0-1
  return n / 100;
}

function toIntBytes(value: number | null | undefined): number | null {
  return value != null ? Math.round(value) : null;
}

function gibToIntBytes(gib: number | null | undefined): number | null {
  return gib != null ? Math.round(gib * BYTES_PER_GIB) : null;
}

export function volumeToExportRowCsv(volume: Volume): Record<string, unknown> {
  const currentSizeBytes = resolveVolumeCurrentSizeBytes(volume);
  const utilization = resolveVolumeUtilization(volume);
  const tagStr = (volume.tags || []).map((t) => `${t.key}:${t.value}`).join(';');

  return {
    'Volume ID': nullForNA(volume.volumeId ?? null),
    'Instance ID': nullForNA(volume.instanceId ?? null),
    'Cluster Name': nullForNA(volume.clusterName ?? null),
    'Node Group': nullForNA(volume.nodeGroupName ?? null),
    'Current Size (bytes)': toIntBytes(currentSizeBytes),
    'Original Size (bytes)': toIntBytes(volume.diskSize),
    'Used Storage (bytes)': currentSizeBytes != null && utilization != null ? Math.round(utilization * currentSizeBytes) : null,
    'Utilization': utilization,
    'Volume Tags': nullForNA(tagStr || null),
    'Autoscaling Active': volume.isDatafiable ?? null,
    'Autoscaling Activation Progress': volume.statusProgress != null ? volume.statusProgress / 100 : null,
    'Unsupported Reason': nullForNA(volume.notDatafiableReason),
  };
}

export function volumeToExportRowJson(volume: Volume): Record<string, unknown> {
  const currentSizeBytes = resolveVolumeCurrentSizeBytes(volume);
  const utilization = resolveVolumeUtilization(volume);
  const tagsMap: Record<string, string> = {};
  for (const t of volume.tags || []) {
    tagsMap[t.key] = t.value;
  }

  return {
    volumeId: nullForNA(volume.volumeId ?? null),
    instanceId: nullForNA(volume.instanceId ?? null),
    clusterName: nullForNA(volume.clusterName ?? null),
    nodeGroupName: nullForNA(volume.nodeGroupName ?? null),
    currentSizeBytes: toIntBytes(currentSizeBytes),
    originalSizeBytes: toIntBytes(volume.diskSize),
    usedStorageBytes: currentSizeBytes != null && utilization != null ? Math.round(utilization * currentSizeBytes) : null,
    utilization,
    tags: tagsMap,
    autoscalingActive: volume.isDatafiable ?? null,
    autoscalingActivationProgress: volume.statusProgress != null ? volume.statusProgress / 100 : null,
    unsupportedReason: nullForNA(volume.notDatafiableReason),
  };
}

/**
 * Note: `action.originalDiskSizeGb` and `action.newDiskSizeGb` are in GiB
 * (despite the `Gb` suffix) as confirmed by SizeCell in VolumeActionsLog.tsx.
 * Values are emitted as integer bytes.
 */
export function optimizationActionToExportRowCsv(
  action: OptLogVolumeActions
): Record<string, unknown> {
  const startTime = nullForNA(action.actionStartTime ?? action.startedTimestamp);
  const endTime = nullForNA(action.actionFinishTime);
  return {
    'Volume ID': nullForNA(action.volumeId ?? null),
    'Instance ID': nullForNA(action.instanceId ?? null),
    'Type': nullForNA(action.type ?? null),
    'Original Size (bytes)': gibToIntBytes(action.originalDiskSizeGb),
    'New Size (bytes)': gibToIntBytes(action.newDiskSizeGb),
    'Start Time': startTime,
    'End Time': endTime,
  };
}

export function optimizationActionToExportRowJson(
  action: OptLogVolumeActions
): Record<string, unknown> {
  const startTime = nullForNA(action.actionStartTime ?? action.startedTimestamp);
  const endTime = nullForNA(action.actionFinishTime);
  return {
    volumeId: nullForNA(action.volumeId ?? null),
    instanceId: nullForNA(action.instanceId ?? null),
    type: nullForNA(action.type ?? null),
    originalSizeBytes: gibToIntBytes(action.originalDiskSizeGb),
    newSizeBytes: gibToIntBytes(action.newDiskSizeGb),
    startTime,
    endTime,
  };
}

export function graphDataPointToExportRowCsv(
  item: GraphDataPoint,
  showFullGraph: boolean
): Record<string, unknown> {
  return {
    'Timestamp': item.ts ?? null,
    'Utilized (bytes)': item.utilized ?? null,
    'Size (bytes)': item.size ?? null,
    ...(showFullGraph ? { 'Datafy Disk Used (bytes)': item.datafyDiskUsed ?? null } : {}),
    'Source Size (bytes)': item.sourceSize ?? null,
  };
}

export function graphDataPointToExportRowJson(
  item: GraphDataPoint,
  showFullGraph: boolean
): Record<string, unknown> {
  return {
    timestamp: item.ts ?? null,
    utilizedBytes: item.utilized ?? null,
    sizeBytes: item.size ?? null,
    ...(showFullGraph ? { datafyDiskUsedBytes: item.datafyDiskUsed ?? null } : {}),
    sourceSizeBytes: item.sourceSize ?? null,
  };
}


